import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Button, ScreenTabs } from "components";
import { Formik } from "formik";
import { getUserSettings } from "services/auth/actions";
import { toastAlert } from "@paylixecommerce/shared/components";
import config from "constants/config";
import * as Yup from "yup";
import { savePersonal } from "./actions";
import { Option, SingleValue } from "./options";
import { TextArea, Text } from "components/fields";
import { Select } from "@paylixecommerce/shared/components";
import PhoneInput from "react-phone-input-2";
import loadable from "@loadable/component";

import '../react-phone-input.scss'
import "../../index.scss";
import "./style.scss";


export const Onfido = loadable(() => import(/* webpackChunkName: "Onfido" */ "./onfido"), { ssr: false });


const PersonalPage = ({ setStep, skipStep, formRef }) => {

    const [type, setType] = useState("INDIVIDUAL");
    const [loading, setLoading] = useState(true);
    const [personalDetails, setPersonal] = useState({
        personal_details_review_details: null,
        personal_details_review_status: null,
        personal_details_under_review: null,
    })
    const [onfidoDetails, setOnfido] = useState({
        onfido_application_status: "",
        onfido_expires_at: null,
        onfido_application_type: null,
        onfido_validation: null
    });

    const [initialValues, setInitialValue] = useState({
        name: "",
        surname: "",
        phone: "",
        phone_country_code: "",
        country_code: null,
        street_address: "",
        additional_address_info: "",
        business_details: "",
        city: "",
        postal_code: "",
        state: ""
    });

    let dispatch = useDispatch();


    const getSettings = () => {
        dispatch(getUserSettings())
            .then((res) => {

                let {
                    name,
                    surname,
                    phone,
                    phone_country_code,
                    country_code,
                    street_address,
                    additional_address_info,
                    business_details,
                    city,
                    postal_code,
                    state,
                    personal_details_review_details,
                    personal_details_review_status,
                    personal_details_under_review,

                    tax_type,
                    sdi_code,
                    vat_number,

                    onfido_application_status,
                    onfido_expires_at,
                    onfido_application_type,
                    onfido_validation
                } = res.data.settings.user;


                setOnfido({
                    onfido_application_status,
                    onfido_expires_at,
                    onfido_application_type,
                    onfido_validation
                })

                setPersonal({
                    personal_details_review_details,
                    personal_details_review_status,
                    personal_details_under_review,
                })

                if(tax_type) {
                    setType(tax_type);
                }

                setInitialValue({
                    name: name || "",
                    surname: surname || "",
                    phone: phone || "",
                    phone_country_code: phone_country_code || "",
                    country_code: country_code,
                    street_address: street_address || "",
                    business_details: business_details || "",
                    additional_address_info: additional_address_info || "",
                    city: city || "",
                    postal_code: postal_code || "",
                    state: state || "",
                    sdi_code: sdi_code || "",
                    vat_number: vat_number || "",
                });
            })
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        getSettings();
    }, []);


    const isIndi = type === "INDIVIDUAL";


    const validationSchema = Yup.object().shape({
        sdi_code: Yup.string(),
        vat_number: Yup.string(),
        name: Yup.string().required('First Name is required'),
        surname: isIndi ? Yup.string().required('Last Name is required') : Yup.string(),
        phone: Yup.string().required('Phone is required').required('Phone Number is required'),
        phone_country_code: Yup.string(),
        country_code:
            Yup.lazy(value => {
                switch (typeof value) {
                    case 'object':
                        return Yup.object().shape({
                            label: Yup.string(),
                            value: Yup.string()
                        }).nullable()
                    case 'string':
                        return Yup.string()
                    default:
                        return Yup.mixed();
                }
            }),
        street_address: Yup.string().required('Address is required'),
        additional_address_info: Yup.string(),
        business_details: Yup.string().required('Business Details is required').min(150, "Minimum chars 150"),
        city: Yup.string().required('City is required'),
        postal_code: Yup.string().required('ZIP is required'),
        state: Yup.string().required('State is required'),
    });

    const onSubmit = (values) => {
      delete values.confirm_password;

      let data = {...values};

      if(typeof data.country_code === "string") {
          data.country_code = values.country_code;
      } else {
          data.country_code = values.country_code.value;
      }

      data.tax_type = type;

      if(!isIndi) {
          delete data.surname;
      }

      setLoading(true);
      dispatch(savePersonal(data))
          .then(res => {
            getSettings();
            if(setStep) {
                setStep()
            } else {
                toastAlert('success', res.message);
            }
          })
          .catch(err => toastAlert('error', (err && err.error) || 'Something went wrong!'))
          .finally(() => {
            setLoading(false)
          })
    };


    return (
      <div className={`settings-content ${skipStep ? 'p-0' : ''}`}>
          <Formik
              innerRef={formRef}
              validationSchema={validationSchema}
              initialValues={initialValues}
              onSubmit={onSubmit}
              enableReinitialize={true}
          >
              {form => {

                  if(document && document.getElementById && document.getElementById('submit-settings')) {
                      let submitButton = document.getElementById('submit-settings');
                      if(form.dirty) {
                          submitButton.classList.remove('d-none');
                          submitButton.classList.add('d-flex');
                      } else {
                          submitButton.classList.remove('d-flex')
                          submitButton.classList.add('d-none')
                      }
                  }

                  let isItaly = form.values.country_code && (form.values.country_code === "IT" || (form.values.country_code.value && form.values.country_code.value === "IT"))
                  let isEU = form.values.country_code && (config.EU_LIST.includes(form.values.country_code) || config.EU_LIST.includes(form.values.country_code.value && form.values.country_code.value));

                  let countrySelectValue =
                      form.values.country_code && typeof form.values.country_code === 'string'
                          ? config.COUNTRIES.find(({ value }) => form.values.country_code === value) :
                          form.values.country_code
                  ;

                  return <form onSubmit={form.handleSubmit}>

                      <div className="mb-4 position-relative">

                          <Onfido onfidoDetails={onfidoDetails} personalDetails={personalDetails} getSettings={getSettings} form={form} />

                          <div className="settings-content-personal">

                              <ScreenTabs
                                  tabs={[{ label: "Company", value: "COMPANY" }, { label: "Individual", value: "INDIVIDUAL" }]}
                                  setActiveTab={setType}
                                  activeTab={type}
                              />

                              <div>
                                  <div className="mb-4 mt-4">
                                      <div className="paylixecommerce-label">Country</div>

                                      <Select
                                          placeholder="Select country"
                                          options={config.COUNTRIES}
                                          isMulti={false}
                                          isSearchable={true}
                                          name="country_code"
                                          value={countrySelectValue}
                                          onChange={(option) => {
                                              form.setFieldValue("country_code", option);
                                          }}
                                          components={{
                                              SingleValue,
                                              Option
                                          }}
                                      />
                                  </div>
                              </div>


                              {
                                  (!isEU && !isItaly && !isIndi) &&
                                  <>
                                      <div className="inputs-block-container">
                                          <Text label="Company Name" type="text" name="name" placeholder="Company Name" {...form}/>
                                          <Text label="City" type="text" name="city" placeholder="City" {...form} />
                                      </div>

                                      <div className="inputs-block-container">
                                          <Text label="State / Province / County" type="text" name="state" placeholder="State / Province / County" {...form} />
                                          <Text label="Zip / Postal code" type="text" name="postal_code" placeholder="Zip / Postal code" {...form} />
                                      </div>
                                  </>
                              }

                              {
                                  (isEU && !isItaly && !isIndi) &&
                                  <div>

                                      <div className="inputs-block-container">
                                          <Text label="Company Name" type="text" name="name" placeholder="Company Name" {...form} />
                                          <Text label="EU VAT Number" type="text" name="vat_number" placeholder="EU VAT Number" {...form} />
                                      </div>

                                      <div className="w-100">
                                          <Text label="City" type="text" name="city" placeholder="City" {...form} />
                                      </div>

                                      <div className="inputs-block-container">
                                          <Text label="State / Province / County" type="text" name="state" placeholder="State / Province / County" {...form} />
                                          <Text label="Zip / Postal code" type="text" name="postal_code" placeholder="Zip / Postal code" {...form} />
                                      </div>
                                  </div>
                              }

                              {
                                  (isItaly && !isIndi) &&
                                  <div>

                                      <div className="inputs-block-container">
                                          <Text label="Company Name" type="text" name="name" placeholder={"Company Name"} {...form}/>
                                          <Text label="SDI Code (Sistema di Interscambio)" type="text" name="sdi_code" placeholder="SDI code" {...form} />
                                      </div>

                                      <div className="inputs-block-container">
                                          <Text label="Partita IVA" type="text" name="vat_number" placeholder="Partita IVA" {...form} />
                                          <Text label="City" type="text" name="city" placeholder="City" {...form} />
                                      </div>

                                      <div className="inputs-block-container">
                                          <Text label="State / Province / County" type="text" name="state" placeholder="State / Province / County" {...form} />
                                          <Text label="Zip / Postal code" type="text" name="postal_code" placeholder="Zip / Postal code" {...form} />
                                      </div>
                                  </div>
                              }

                              {
                                  isIndi &&
                                      <div>

                                          <div className="inputs-block-container">
                                              <Text label="Name" type="text" name="name" placeholder={"First Name"} {...form}/>
                                              <Text label="Surname" type="text" name="surname" placeholder="Last Name" {...form} />
                                          </div>

                                          <div className="inputs-block-container">
                                              <Text label="City" type="text" name="city" placeholder="City" {...form} />
                                              <Text label="State / Province / County" type="text" name="state" placeholder="State / Province / County" {...form} />
                                          </div>

                                          <div className="d-flex">
                                              <div className="w-100">
                                                  <Text label="Zip / Postal code" type="text" name="postal_code" placeholder="Zip / Postal code" {...form} />
                                              </div>
                                          </div>
                                      </div>
                              }


                              <Text label="Address, House/Building number" type="text" name="street_address" placeholder="Street address, House/Building number" {...form} />
                              <Text label={<>Additional address details&nbsp;<small>(optional)</small></>} type="text" name="additional_address_info" placeholder="Apartment / Suite / Unit / Building / Floor etc." {...form} />

                              <div className="my-3">
                                  <label htmlFor="" className="paylixecommerce-label">Phone Number</label>
                                  <PhoneInput
                                      // country={form.values.phone_country_code || ""}
                                      containerClass={form.errors.phone && form.touched.phone ? 'is-invalid' : ''}
                                      value={form.values.phone}
                                      onChange={(a, b) => {
                                          form.setFieldValue("phone", a);
                                          form.setFieldValue("phone_country_code", b.countryCode ? b.countryCode.toUpperCase() : "");
                                          form.setFieldValue("phone_dial_code", b.dialCode);
                                      }}
                                  />
                                  {form.errors.phone ? <div className="invalid-feedback d-flex">{form.errors.phone}</div> : null}
                              </div>


                              <TextArea
                                  wrapperClassName="business-details"
                                  label={isIndi ? "Personal Details" : "Business Details"}
                                  type="text"
                                  name="business_details"
                                  placeholder="Business Details"
                                  noteUnder
                                  note={
                                      isIndi ?
                                          "Fill the next input with information about your Paylix usage. Specify what market type are you in and what products you sell. This helps us better understanding your use case and configure your business accordingly.\n" :
                                          "Fill the next input with information about your business. Specify what market type are you in and what products you sell. This helps us better understanding your use case and configure your business accordingly. "
                                  }
                                  {...form}
                              />
                          </div>

                      </div>

                      {skipStep ? <div className="setup-screen-step-buttons">
                              <Button variant="secondary" size="large" type="button" onClick={skipStep}>
                                  Skip Step
                              </Button>
                              <Button variant="primary" size="large" type="submit">
                                  End Setup
                              </Button>
                          </div>
                          :
                          null
                      }
                  </form>
              }}
          </Formik>
      </div>
    )

}

export default PersonalPage;
